'use client'

import { nanoid } from 'nanoid'
import type {
  PromptInputAttachment,
  PromptInputAttachmentInput,
} from '@/components/ai-elements/prompt-input'
import {
  type PendingPrompt,
  PUBLIC_COMPOSER_DRAFT_KEY,
  type PublicComposerDraft,
  type PublicComposerDraftAttachment,
} from './types'

const PUBLIC_COMPOSER_DB = 'orchestrator-public-composer'
const PUBLIC_COMPOSER_FILES_STORE = 'files'

export type PublicComposerLocalFile = {
  id: string
  file: File
  previewText?: string
  isHtmlAttachment?: boolean
}

export type ComposerRestoreAction =
  | { type: 'none' }
  | { type: 'autosend-pending'; pending: PendingPrompt }
  | { type: 'prefill-public'; draft: PublicComposerDraft }

function hasIndexedDb() {
  return typeof window !== 'undefined' && 'indexedDB' in window
}

function openPublicComposerDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(PUBLIC_COMPOSER_DB, 1)

    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(PUBLIC_COMPOSER_FILES_STORE)) {
        database.createObjectStore(PUBLIC_COMPOSER_FILES_STORE)
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function runStoreRequest<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return openPublicComposerDb().then(
    (database) =>
      new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(
          PUBLIC_COMPOSER_FILES_STORE,
          mode
        )
        const store = transaction.objectStore(PUBLIC_COMPOSER_FILES_STORE)
        const request = action(store)

        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
        transaction.oncomplete = () => database.close()
        transaction.onerror = () => reject(transaction.error)
      })
  )
}

export function toPublicComposerDraftAttachments(
  files: Array<{
    id: string
    filename?: string
    mediaType?: string
    previewText?: string
    isHtmlAttachment?: boolean
  }>
): Array<PublicComposerDraftAttachment> {
  return files.map((file) => ({
    id: file.id,
    filename: file.filename || 'attachment',
    mediaType: file.mediaType || 'application/octet-stream',
    ...(file.previewText ? { previewText: file.previewText } : {}),
    ...(file.isHtmlAttachment ? { isHtmlAttachment: true } : {}),
  }))
}

export function buildPublicComposerDraft(args: {
  chatId: string
  text: string
  filesKey?: string
  customBrandId?: string | null
  remixSourceFlowSlug?: string
  files: Array<{
    id: string
    filename?: string
    mediaType?: string
    previewText?: string
    isHtmlAttachment?: boolean
  }>
}): PublicComposerDraft {
  const storedFiles =
    args.files.length > 0 ? toPublicComposerDraftAttachments(args.files) : []

  return {
    chatId: args.chatId,
    text: args.text,
    ...(args.filesKey ? { filesKey: args.filesKey } : {}),
    ...(storedFiles.length > 0 ? { files: storedFiles } : {}),
    ...(args.customBrandId ? { customBrandId: args.customBrandId } : {}),
    ...(args.remixSourceFlowSlug
      ? { remixSourceFlowSlug: args.remixSourceFlowSlug }
      : {}),
  }
}

export function readPublicComposerDraft(): PublicComposerDraft | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const stored = localStorage.getItem(PUBLIC_COMPOSER_DRAFT_KEY)
    if (!stored) {
      return null
    }

    return JSON.parse(stored) as PublicComposerDraft
  } catch {
    return null
  }
}

export function writePublicComposerDraft(draft: PublicComposerDraft | null) {
  if (typeof window === 'undefined') {
    return
  }

  if (!draft) {
    localStorage.removeItem(PUBLIC_COMPOSER_DRAFT_KEY)
    return
  }

  localStorage.setItem(PUBLIC_COMPOSER_DRAFT_KEY, JSON.stringify(draft))
}

export function createPublicComposerFilesKey(chatId: string) {
  return `${chatId}:${nanoid()}`
}

export async function writePublicComposerDraftFiles(
  filesKey: string,
  files: Array<PublicComposerLocalFile>
) {
  if (!hasIndexedDb() || !filesKey) {
    return
  }

  await runStoreRequest('readwrite', (store) => store.put(files, filesKey))
}

export async function readPublicComposerDraftFiles(
  filesKey?: string
): Promise<Array<PublicComposerLocalFile>> {
  if (!hasIndexedDb() || !filesKey) {
    return []
  }

  try {
    const result = await runStoreRequest<
      Array<PublicComposerLocalFile> | undefined
    >('readonly', (store) => store.get(filesKey))
    return Array.isArray(result) ? result : []
  } catch {
    return []
  }
}

export async function deletePublicComposerDraftFiles(filesKey?: string) {
  if (!hasIndexedDb() || !filesKey) {
    return
  }

  try {
    await runStoreRequest('readwrite', (store) => store.delete(filesKey))
  } catch {
    // Ignore storage cleanup failures.
  }
}

export async function clearPublicComposerDraftStorage(filesKey?: string) {
  writePublicComposerDraft(null)
  await deletePublicComposerDraftFiles(filesKey)
}

export function toPromptInputAttachmentInputs(
  files: Array<PublicComposerLocalFile>
): Array<PromptInputAttachmentInput> {
  return files.map((file) => ({
    file: file.file,
    ...(file.previewText ? { previewText: file.previewText } : {}),
    ...(file.isHtmlAttachment ? { isHtmlAttachment: true } : {}),
  }))
}

export function toDraftLocalFiles(
  attachments: Array<PromptInputAttachment>
): Array<PublicComposerLocalFile> {
  return attachments.flatMap((attachment) =>
    attachment.file
      ? [
          {
            id: attachment.id,
            file: attachment.file,
            ...(attachment.previewText
              ? { previewText: attachment.previewText }
              : {}),
            ...(attachment.isHtmlAttachment ? { isHtmlAttachment: true } : {}),
          },
        ]
      : []
  )
}

export function resolveComposerRestoreAction(args: {
  boxOnly: boolean
  chatId?: string
  initialMessagesCount: number
  pendingPrompt: PendingPrompt | null
  publicDraft: PublicComposerDraft | null
}): ComposerRestoreAction {
  if (args.boxOnly || !args.chatId || args.initialMessagesCount > 0) {
    return { type: 'none' }
  }

  if (args.pendingPrompt?.chatId === args.chatId) {
    return { type: 'autosend-pending', pending: args.pendingPrompt }
  }

  if (args.publicDraft?.chatId === args.chatId) {
    return { type: 'prefill-public', draft: args.publicDraft }
  }

  return { type: 'none' }
}

export function getPublicComposerRedirectChatId(
  draft: PublicComposerDraft | null
) {
  return draft?.chatId || null
}
